import React from "react";
import PropTypes from "prop-types";
import { Controller } from "react-hook-form";
import { CheckIcon } from "@heroicons/react/24/outline";

const CommonCheckbox = ({
  name,
  control,
  label = "",
  disabled = false,
  defaultValue = false,
  onChange,
  fontSize = "text-sm",
  checkboxSize = "h-4 w-4",
  checkboxColor = "bg-blue-500",
  borderColor = "border-gray-300",
  textColor = "text-gray-700",
  error,
  className = "",
  ...rest
}) => {
  const getCheckboxSizeClass = () => {
    switch (checkboxSize) {
      case "h-3 w-3":
      case "h-4 w-4":
      case "h-5 w-5":
      case "h-6 w-6":
        return checkboxSize;
      default:
        return "h-4 w-4";
    }
  };

  const getFontSizeClass = () => {
    switch (fontSize) {
      case "text-xs":
      case "text-sm":
      case "text-base":
      case "text-lg":
        return fontSize;
      default:
        return "text-sm";
    }
  };

  return (
    <div className={`relative w-full ${className}`}>
      <Controller
        name={name}
        control={control}
        defaultValue={defaultValue}
        render={({
          field: { onChange: fieldOnChange, value, ref },
          fieldState,
        }) => {
          const hasError = Boolean(fieldState.error || error);
          const isChecked = Boolean(value);

          return (
            <>
              <div className="flex items-center">
                {/* Hidden Input */}
                <input
                  ref={ref}
                  type="checkbox"
                  id={name}
                  className="sr-only"
                  checked={isChecked}
                  onChange={(e) => {
                    fieldOnChange(e.target.checked);
                    if (onChange) onChange(e.target.checked);
                  }}
                  disabled={disabled}
                  {...rest}
                />

                {/* Custom Checkbox */}
                <div
                  className={`${getCheckboxSizeClass()} border-2 rounded cursor-pointer flex items-center justify-center transition-all duration-200
                    ${
                      hasError
                        ? "border-red-500"
                        : isChecked
                        ? `${checkboxColor} border-transparent`
                        : `${borderColor} hover:border-[#00838F]`
                    }
                    ${
                      disabled
                        ? "opacity-50 cursor-not-allowed"
                        : "hover:scale-105"
                    }`}
                  onClick={() => {
                    if (!disabled) {
                      fieldOnChange(!isChecked);
                      if (onChange) onChange(!isChecked);
                    }
                  }}
                >
                  {isChecked && (
                    <CheckIcon
                      className={`${
                        checkboxSize === "h-3 w-3"
                          ? "h-2 w-2"
                          : checkboxSize === "h-5 w-5"
                          ? "h-4 w-4"
                          : checkboxSize === "h-6 w-6"
                          ? "h-5 w-5"
                          : "h-3 w-3"
                      } text-white font-bold`}
                    />
                  )}
                </div>

                {/* Label */}
                {label && (
                  <label
                    htmlFor={name}
                    className={`ml-2 select-none cursor-pointer ${getFontSizeClass()} ${
                      hasError
                        ? "text-red-500"
                        : disabled
                        ? "text-gray-400"
                        : textColor
                    }`}
                    onClick={() => {
                      if (!disabled) {
                        fieldOnChange(!isChecked);
                        if (onChange) onChange(!isChecked);
                      }
                    }}
                  >
                    {label}
                  </label>
                )}
              </div>

              {/* Error Message */}
              {hasError && (
                <p className="mt-1 text-sm text-red-600">
                  {fieldState.error?.message ||
                    error?.message ||
                    "This field is required"}
                </p>
              )}
            </>
          );
        }}
      />
    </div>
  );
};

CommonCheckbox.propTypes = {
  name: PropTypes.string.isRequired,
  control: PropTypes.object.isRequired,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  defaultValue: PropTypes.bool,
  onChange: PropTypes.func,
  fontSize: PropTypes.string,
  checkboxSize: PropTypes.string,
  checkboxColor: PropTypes.string,
  borderColor: PropTypes.string,
  textColor: PropTypes.string,
  error: PropTypes.object,
  className: PropTypes.string,
};

export default CommonCheckbox;
